import React from 'react'
import { Helmet } from 'react-helmet'
import styled, { createGlobalStyle } from 'styled-components'

const accent = '#2f9d84'

const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
`

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fafafa;
  color: #262626;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, Arial, sans-serif;
`

const monospace = `
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas,
    'Liberation Mono', monospace;
`

const Container = styled.main`
  max-width: 720px;
  margin: 0 auto;
  padding: 80px 24px 120px;
`

const Eyebrow = styled.p`
  ${monospace}
  margin: 0 0 16px;
  color: ${accent};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const Title = styled.h1`
  margin: 0;
  font-size: clamp(2.4rem, 6vw, 3.4rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
`

const UpdatedAt = styled.p`
  ${monospace}
  margin: 16px 0 0;
  color: #8a8a8a;
  font-size: 0.95rem;
`

const Callout = styled.div`
  margin: 40px 0 8px;
  padding: 24px 28px;
  border-left: 4px solid ${accent};
  border-radius: 4px;
  background: #f2f3f4;
  font-size: 1.05rem;
  line-height: 1.6;

  b {
    font-weight: 700;
  }
`

const Section = styled.section`
  margin-top: 56px;
`

const SectionHeading = styled.h2`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 0 0 20px;
  font-size: 1.4rem;
  font-weight: 700;
`

const SectionNumber = styled.span`
  ${monospace}
  color: ${accent};
  font-size: 0.95rem;
  font-weight: 700;
`

const Paragraph = styled.p`
  margin: 0 0 18px;
  font-size: 1.05rem;
  line-height: 1.65;
  color: #3d3d3d;

  &:last-child {
    margin-bottom: 0;
  }
`

const List = styled.ul`
  margin: 0 0 18px;
  padding-left: 22px;
  font-size: 1.05rem;
  line-height: 1.65;
  color: #3d3d3d;
`

const ListItem = styled.li`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  b {
    font-weight: 700;
  }
`

const Code = styled.code`
  ${monospace}
  padding: 2px 6px;
  border-radius: 4px;
  background: #eef0f1;
  font-size: 0.92em;
`

const A = styled.a`
  color: ${accent};
  text-decoration: underline;
`

const Footer = styled.p`
  margin-top: 64px;
  padding-top: 32px;
  border-top: 1px solid #e5e6e8;
  font-size: 0.95rem;
  color: #8a8a8a;
`

const PrivacyPolicyPage = () => {
  const title = 'Privacy Policy — Life Fasting'
  const description = 'Privacy policy for the Life Fasting Android app.'

  return (
    <Wrapper>
      <GlobalStyle />
      <Helmet
        htmlAttributes={{ lang: 'en' }}
        title={title}
        meta={[{ name: 'description', content: description }]}
      />
      <Container>
        <Eyebrow>Life Fasting</Eyebrow>
        <Title>Privacy Policy</Title>
        <UpdatedAt>Last updated: 18 August 2026</UpdatedAt>

        <Callout>
          <b>Life Fasting does not collect, transmit or share any personal data.</b>{' '}
          It has no account system, no analytics and no advertising, requests
          no Android permissions, and makes no network requests of its own.
          Everything you record stays on your device.
        </Callout>

        <Section>
          <SectionHeading>
            <SectionNumber>01</SectionNumber> Who this policy is from
          </SectionHeading>
          <Paragraph>
            Life Fasting is developed and published by Dominik Kobylarz, an
            individual developer, trading as kobylarz.co. Contact:{' '}
            <A href="mailto:dominik.w.kobylarz@gmail.com">
              dominik.w.kobylarz@gmail.com
            </A>
            .
          </Paragraph>
          <Paragraph>
            This policy covers the Android application{' '}
            <Code>co.kobylarz.lifefasting</Code>, distributed through Google
            Play.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>02</SectionNumber> What the app stores
          </SectionHeading>
          <Paragraph>
            The app stores the following on your device, in its own private
            storage:
          </Paragraph>
          <List>
            <ListItem>
              <b>Your fasts</b> — for each one, the start time, the end time
              (once finished), and the goal duration you set. Nothing else.
            </ListItem>
            <ListItem>
              <b>Your settings</b> — the language and theme you chose.
            </ListItem>
          </List>
          <Paragraph>
            There is no name, email address, account, device identifier,
            location, contact, photo, health-platform record or advertising
            ID involved at any point. The app never asks for any of these and
            has no means of reading them: it declares no Android permissions
            whatsoever.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>03</SectionNumber> What leaves your device
          </SectionHeading>
          <Paragraph>
            The app itself makes no network requests. It contains no
            analytics, crash reporting, advertising or tracking libraries of
            any kind.
          </Paragraph>
          <Paragraph>
            Two things can nonetheless result in data leaving your device:
          </Paragraph>
          <List>
            <ListItem>
              <b>Links you tap.</b> The scientific references in the fasting
              timeline, the &ldquo;Buy me a coffee&rdquo; button and the
              &ldquo;Rate on Google Play&rdquo; button all hand a web address
              to your browser. The app sends nothing with them, but the site
              you land on — a journal publisher, Buy Me a Coffee, or Google
              Play — then receives a request from your browser and applies
              its own privacy policy. No information about your fasts is
              included.
            </ListItem>
            <ListItem>
              <b>Android backup.</b> The app permits Android&rsquo;s standard
              backup service, so your fasting history may be included in the
              device backup your phone makes to your own Google account. This
              is a Google service operating under Google&rsquo;s privacy
              policy, not something the app transmits or can read. You can
              disable it in your device&rsquo;s system settings under Backup.
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>04</SectionNumber> Health information
          </SectionHeading>
          <Paragraph>
            Records of when you fasted may be considered health-related. They
            are treated the same as everything else here: stored only on
            your device, never transmitted by the app, never shared, and
            never used for any purpose beyond showing them back to you.
          </Paragraph>
          <Paragraph>
            The app does not connect to Health Connect, Google Fit, or any
            other health platform.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>05</SectionNumber> Deleting your data
          </SectionHeading>
          <Paragraph>
            Individual fasts can be deleted from the History tab. Uninstalling
            the app removes its database and settings from your device
            entirely.
          </Paragraph>
          <Paragraph>
            Because nothing is ever sent anywhere, there is no server-side
            copy to request the deletion of, and no account to close. If a
            device backup exists in your Google account, it is managed
            through your Google account settings.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>06</SectionNumber> Children
          </SectionHeading>
          <Paragraph>
            The app is not directed at children and collects no data from
            anyone, including children. Fasting is not appropriate for
            children without medical supervision.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>07</SectionNumber> Changes to this policy
          </SectionHeading>
          <Paragraph>
            If a future version of the app changes what it stores or
            transmits, this policy will be updated before that version is
            released, and the date at the top will change. Material changes
            will also be noted in the release notes on Google Play.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>
            <SectionNumber>08</SectionNumber> Not medical advice
          </SectionHeading>
          <Paragraph>
            Life Fasting is informational and based on published scientific
            literature. It is not medical advice and does not replace a
            doctor. Fasting beyond 24–48 hours carries real risks,
            particularly for anyone taking medication — especially insulin or
            sulfonylureas — anyone with a history of eating disorders, and
            anyone who is pregnant or breastfeeding. Consult a doctor before
            undertaking extended fasts.
          </Paragraph>
        </Section>

        <Footer>
          Questions about this policy:{' '}
          <A href="mailto:dominik.w.kobylarz@gmail.com">
            dominik.w.kobylarz@gmail.com
          </A>
        </Footer>
      </Container>
    </Wrapper>
  )
}

export default PrivacyPolicyPage
